// Proof of delivery / pickup records: ONE copy of the requests, used by the desktop Sales Order
// page's photo panel (components/PodPanel.jsx) and the phone order screen's camera photos
// (mobile/screens/salesOrders/PodSheet.jsx). docs/mobile-pwa-plan.md "One copy of every action".
// Moved from PodPanel word for word; PodPanel.actions.test.jsx pins the requests.
//
// Upload path: one presign call → PUT each file straight to the private bucket (XHR, for
// progress) → ONE confirm call per photo, in order. Nothing goes through the Lambda, so a
// full-size phone photo never meets the gateway's 4.6 MB ceiling. The API reads each photo's EXIF
// (taken-at, GPS) at confirm time: upload the camera's original file, never a re-encoded copy.
import config from '../../config.js';
import { detailToString } from '../../utils/apiError';

const API_BASE = config.API_BASE_URL;
const noop = () => {};

export const POD_KIND_LABEL = Object.freeze({ delivery: 'Delivery', pickup: 'Customer pickup', carrier_handoff: 'Carrier handoff' });

/** The record kind an order starts with: pickup orders record a pickup, the rest a delivery. */
export function defaultKindFor(fulfillmentType) {
  return fulfillmentType === 'pickup' ? 'pickup' : 'delivery';
}

/** A document on the order that is the signed manifest. */
export function isManifestDoc(doc) {
  const n = (doc?.display_name || '').toLowerCase();
  const t = (doc?.doc_type || '').toLowerCase();
  return n.startsWith('signed manifest') || t === 'shipping receipt';
}

/** Local ISO (datetime-local input value) → ISO string with the browser's offset. */
export function localInputToIso(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** A JSON request; a refusal throws its detail. */
export async function apiJson(url, opts = {}) {
  const res = await fetch(url, { headers: { 'Content-Type': 'application/json' }, ...opts });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(detailToString(body.detail) || `HTTP ${res.status}`);
  return body;
}

export function putToS3WithProgress(putUrl, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', putUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Storage refused the upload (HTTP ${xhr.status}).`)));
    xhr.onerror = () => reject(new Error('The upload to storage failed (network).'));
    xhr.send(file);
  });
}

/** The plan's delivery records (GET /packaging/plans/{id}/pod): {events, limits}. Throws. */
export function loadPod(planId) {
  return apiJson(`${API_BASE}/packaging/plans/${planId}/pod`);
}

/** The order's documents (to link the signed manifest); [] when they can't be read. */
export async function loadOrderDocuments(soId) {
  const res = await fetch(`${API_BASE}/sales_orders/${soId}/documents`);
  return res.ok ? res.json() : [];
}

/** Upload one file to an event: PUT to storage, then confirm (the API reads the photo). Reports
 *  progress through onFile(patch); never throws (a failure is reported as {status:'failed'}). */
async function uploadOne({ planId, eventId, pin, spec, file, onFile }) {
  onFile({ status: 'uploading', progress: 0, error: '' });
  try {
    await putToS3WithProgress(spec.put_url, file, spec.content_type, (pct) => onFile({ progress: pct }));
    onFile({ status: 'processing' });
    const r = await apiJson(`${API_BASE}/packaging/plans/${planId}/pod/events/${eventId}/photos/${spec.photo_id}/confirm`, {
      method: 'POST', body: JSON.stringify({ pin }),
    });
    if (r.ok) onFile({ status: 'done', progress: 100 });
    else onFile({ status: 'failed', error: r.error || 'processing failed' });
  } catch (e) {
    onFile({ status: 'failed', error: e.message || String(e) });
  }
}

/** Create a record (mode 'new'), save an edit ('edit'), or add photos ('add'), then upload
 *  `uploads` ([{file, kind: 'photo'|'manifest'}]) one at a time: each confirm decodes a full-size
 *  photo on the server. onFile(index, patch) reports each upload's status. Resolves to {eventId};
 *  throws when the record or the presign is refused (no file is uploaded then).
 *  fields: {kind, palletIds, occurredAt (a datetime-local value or ''), receivedBy, notes, manifestDocId}. */
export async function saveDeliveryRecord({ planId, mode, event, pin, fields, uploads = [], onFile = noop }) {
  const { kind, palletIds, occurredAt = '', receivedBy = '', notes = '', manifestDocId = '' } = fields || {};
  let eventId = event?.event_id;
  const occurredIso = localInputToIso(occurredAt);
  if (mode === 'new') {
    const created = await apiJson(`${API_BASE}/packaging/plans/${planId}/pod/events`, {
      method: 'POST',
      body: JSON.stringify({
        pin, kind, pallet_ids: palletIds, occurred_at: occurredIso,
        received_by: receivedBy || null, notes: notes || null,
        signed_manifest_document_id: manifestDocId ? Number(manifestDocId) : null,
      }),
    });
    eventId = created.event_id;
  } else if (mode === 'edit') {
    const body = { pin, kind, pallet_ids: palletIds, received_by: receivedBy || '', notes: notes || '' };
    if (occurredIso) body.occurred_at = occurredIso; else body.clear_occurred_at = !!event?.occurred_at && !occurredAt;
    if (manifestDocId) body.signed_manifest_document_id = Number(manifestDocId); else body.clear_manifest = true;
    await apiJson(`${API_BASE}/packaging/plans/${planId}/pod/events/${eventId}`, { method: 'PATCH', body: JSON.stringify(body) });
  }
  if (uploads.length) {
    const presign = await apiJson(`${API_BASE}/packaging/plans/${planId}/pod/events/${eventId}/photos/upload-urls`, {
      method: 'POST',
      body: JSON.stringify({
        pin,
        files: uploads.map((u) => ({ filename: u.file.name, content_type: u.file.type || '', bytes: u.file.size, kind: u.kind })),
      }),
    });
    const specs = presign.photos || [];
    for (let k = 0; k < uploads.length; k++) {
      // eslint-disable-next-line no-await-in-loop
      await uploadOne({ planId, eventId, pin, spec: specs[k], file: uploads[k].file, onFile: (patch) => onFile(k, patch) });
    }
  }
  return { eventId };
}
